// client/src/pages/TaskDetails.jsx
// Clean, stable, modern, responsive Task Details page.
// Light/Dark mode fully supported.
// Works with your existing backend routes.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const LOGO = "/mnt/data/logo_1.png"; // your logo file path

export default function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const isOwner = task && user && String(task.postedBy) === String(user.id);
  const isAccepted =
    task && task.acceptedApplicant && String(task.acceptedApplicant) === String(user?.id);
  const applicants = task?.applicants || [];
  const alreadyApplied = applicants.some((a) => String(a.userId) === String(user?.id));

  // Load task
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/${taskId}`);
        const data = await res.json();
        setTask(data);
      } catch {
        alert("Failed to load task");
      } finally {
        setLoading(false);
      }
    })();
  }, [taskId]);

  function openChat(participantId) {
    navigate(`/chat/${taskId}/${participantId}`);
  }

  async function sendAction(endpoint, body = {}) {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("cp_token");

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Action failed");

      // refresh task
      const res2 = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/${taskId}`);
      const updated = await res2.json();
      setTask(updated);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function deleteTask() {
    if (!window.confirm("Are you sure? This cannot be undone.")) return;

    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/${taskId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Delete failed");

      navigate("/dashboard");
    } catch (err) {
      alert(err.message);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <img src={LOGO} alt="loading" className="w-12 h-12 opacity-80" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="text-xl text-red-500">Task not found</div>
          <button
            onClick={() => navigate("/tasks")}
            className="mt-4 px-4 py-2 rounded-md bg-indigo-600 text-white"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 px-6 py-8">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{task.title}</div>
            <div className="text-sm text-slate-500 dark:text-slate-300 mt-1">
              {task.category || "General"} • Posted on{" "}
              {new Date(task.createdAt).toLocaleDateString()}
            </div>
          </div>

          <button
            onClick={() => navigate("/tasks")}
            className="px-4 py-2 border rounded-md dark:border-slate-700"
          >
            Back
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-6">
            {/* Description Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow">
              <div className="text-sm text-slate-500 dark:text-slate-300">Description</div>
              <div className="mt-2 text-slate-800 dark:text-white whitespace-pre-line text-sm">
                {task.description || "No description provided."}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-900">
                  <div className="text-xs text-slate-500">Budget</div>
                  <div className="text-sm font-semibold mt-1 text-slate-900 dark:text-white">
                    ₹{task.budgetMin}
                    {task.budgetMax ? ` - ₹${task.budgetMax}` : ""}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-900">
                  <div className="text-xs text-slate-500">Deadline</div>
                  <div className="text-sm font-semibold mt-1 text-slate-900 dark:text-white">
                    {task.dueDate
                      ? new Date(task.dueDate).toLocaleString()
                      : "No deadline"}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-900">
                  <div className="text-xs text-slate-500">Applicants</div>
                  <div className="text-sm font-semibold mt-1 text-slate-900 dark:text-white">
                    {applicants.length}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-900">
                  <div className="text-xs text-slate-500">Status</div>
                  <div
                    className={`text-sm font-semibold mt-1 ${
                      task.status === "open"
                        ? "text-green-600"
                        : "text-yellow-500"
                    }`}
                  >
                    {task.status}
                  </div>
                </div>
              </div>
            </div>

            {/* Applicants */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow">
              <div className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Applicants
              </div>

              {applicants.length === 0 ? (
                <div className="text-sm text-slate-500 dark:text-slate-300">
                  No applicants yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {applicants.map((a) => (
                    <div
                      key={a.userId}
                      className="p-3 rounded-lg bg-slate-100 dark:bg-slate-900 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-200 dark:bg-indigo-900 flex items-center justify-center font-semibold text-indigo-700 dark:text-indigo-300">
                          {a.name?.[0] || "U"}
                        </div>
                        <div>
                          <div className="text-sm text-slate-900 dark:text-white font-medium">
                            {a.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-300">
                            {a.email}
                          </div>
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="flex items-center gap-2">
                        {isOwner ? (
                          <>
                            {task.acceptedApplicant === a.userId ? (
                              <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                                Accepted
                              </div>
                            ) : (
                              <>
                                <button
                                  disabled={actionLoading}
                                  onClick={() =>
                                    sendAction(
                                      `${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/accept`,
                                      { applicantId: a.userId }
                                    )
                                  }
                                  className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md"
                                >
                                  Accept
                                </button>

                                <button
                                  disabled={actionLoading}
                                  onClick={() =>
                                    sendAction(
                                      `${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/reject`,
                                      { applicantId: a.userId }
                                    )
                                  }
                                  className="px-3 py-1 border rounded-md text-sm"
                                >
                                  Remove
                                </button>
                              </>
                            )}

                            <button
                              onClick={() => openChat(a.userId)}
                              className="px-3 py-1 border rounded-md text-sm"
                            >
                              Chat
                            </button>
                          </>
                        ) : (
                          <>
                            {String(a.userId) === String(user?.id) && (
                              <button
                                disabled={actionLoading}
                                onClick={() =>
                                  sendAction(
                                    `${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/cancel`
                                  )
                                }
                                className="px-3 py-1 border rounded-md text-sm"
                              >
                                Cancel
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN — ACTION BOX */}
          <div className="lg:col-span-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow sticky top-24">
              {isOwner ? (
                <>
                  <div className="text-sm text-slate-500 mb-2">Owner Actions</div>

                  <button
                    onClick={deleteTask}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-rose-600 text-white rounded-lg shadow mb-3"
                  >
                    Delete Task
                  </button>

                  {task.acceptedApplicant && (
                    <button
                      onClick={() => openChat(task.acceptedApplicant)}
                      className="w-full px-4 py-2 border rounded-lg dark:border-slate-700"
                    >
                      Open Chat
                    </button>
                  )}
                </>
              ) : (
                <>
                  {isAccepted ? (
                    <>
                      <div className="text-green-600 text-sm font-semibold mb-4">
                        You have been accepted
                      </div>

                      <button
                        onClick={() => openChat(user.id)}
                        className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg shadow"
                      >
                        Open Chat
                      </button>
                    </>
                  ) : (
                    <>
                      {!alreadyApplied ? (
                        <button
                          disabled={actionLoading}
                          onClick={() =>
                            sendAction(
                              `${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/apply`
                            )
                          }
                          className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg shadow"
                        >
                          Apply
                        </button>
                      ) : (
                        <button
                          disabled={actionLoading}
                          onClick={() =>
                            sendAction(
                              `${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/cancel`
                            )
                          }
                          className="w-full px-4 py-2 border rounded-lg dark:border-slate-700"
                        >
                          Cancel Application
                        </button>
                      )}

                      <button
                        onClick={() => openChat(task.postedBy)}
                        className="w-full px-4 py-2 border rounded-lg dark:border-slate-700 mt-3"
                      >
                        Chat with Owner
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
